import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getPageTitle } from "../../utils/getPageTitle";
import Button from "../../components/common/button";
import CaseStudyData from "../../components/caseStudyData";
import { useViewCaseStudyQuery } from "../../hooks/caseStudy/view.list.caseStudy";
import { useCaseStudyDelete } from "../../hooks/caseStudy/delete.casestudy";
import SearchItem from "../../components/searchItem";
import Pagination from "../../components/common/pagination";
import { toast } from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useViewSubscription } from "../../hooks/subscription/subscription.view";
import {
  getPlanCreationLimits,
  getPlanLimitMessage,
} from "../subscription/planLimits";
import DeleteModal from "../../components/modal/deleteModal";
import "../../assets/styles/products.css";
import "../../assets/styles/pagination.css";

const CaseStudy = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState("create");
  const { mutate: deleteCaseStudy, isLoading: isDeleting } =
    useCaseStudyDelete();
  const { data: subscriptionResponse, isPlaceholderData } = useViewSubscription();
  const planLimits = getPlanCreationLimits(subscriptionResponse, {
    isReady: !isPlaceholderData,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCases, setSelectedCasestudy] = useState(null);
  const [localCaseStudies, setLocalCaseStudies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalDocs: 0,
    limit: pageSize,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const {
    data: caseStudyResponse,
    isLoading,
    isFetching,
    isError,
  } = useViewCaseStudyQuery({
    page,
    limit: pageSize,
    q: debouncedSearchTerm,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!caseStudyResponse) return;
    setLocalCaseStudies(caseStudyResponse.caseStudies ?? []);
    const apiPagination = caseStudyResponse.pagination;
    if (apiPagination) {
      setPagination({
        currentPage: apiPagination.currentPage || 1,
        totalPages: apiPagination.totalPages || 1,
        totalDocs: apiPagination.total || 0,
        limit: apiPagination.limit || pageSize,
        hasNextPage: apiPagination.hasNextPage || false,
        hasPrevPage: apiPagination.hasPrevPage || false,
      });
    }
  }, [caseStudyResponse, pageSize]);

  const handlePageChange = (newPage) => setPage(newPage);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const handleEdit = (item) => {
    const id = item._id || item.id;
    navigate(`/case-studies/edit/${id}`);
  };

  const handleDelete = (client) => {
    setSelectedCasestudy(client);
    setMode("delete");
    setIsModalOpen(true);
  };

  const handleSave = () => {
    if (mode === "delete" && selectedCases) {
      const id = selectedCases._id || selectedCases.id;

      deleteCaseStudy(id, {
        onSuccess: () => {
          toast.success("Case study deleted successfully");
          setIsModalOpen(false);
          setSelectedCasestudy(null);
          queryClient.invalidateQueries({ queryKey: ["viewCaseStudy"] });
        },
        onError: () => {
          toast.error("Failed to delete case study");
        },
      });
    }
  };

  const handleAdd = () => {
    if (!planLimits.caseStudies.canCreate) {
      toast.error(getPlanLimitMessage("caseStudies", planLimits));
      return;
    }
    navigate("/case-studies/add-caseStudy");
  };

  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error loading case studies</div>;

  return (
    <>
      <div className="page-body">
        <div className="page-header">
          <h1 className="page-title">{getPageTitle(location.pathname)}</h1>
          <p style={{ color: "rgb(85, 85, 85)", fontSize: "13px" }}>
            Manage your Case Studies
          </p>
        </div>
        <div className="page-body-actions">
          <Button
            onClick={handleAdd}
            disabled={!planLimits.caseStudies.canCreate}
          >
            + Add Case Study
          </Button>
          <p
            className={`plan-limit-hint${planLimits.caseStudies.canCreate ? "" : " is-limit"}`}
          >
            {getPlanLimitMessage("caseStudies", planLimits)}
          </p>
        </div>
      </div>

      <div className="list-toolbar">
        <div className="list-toolbar-row">
          <SearchItem
            data={localCaseStudies}
            searchField={["title", "description"]}
            placeholder="Search case studies..."
            wrapSection={false}
            onResultsChange={({ searchItem }) => setSearchTerm(searchItem)}
          />
          <label className="list-toolbar-page-size">
            <span>Items per page:</span>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={20}>20</option>
            </select>
          </label>
        </div>
      </div>

      <CaseStudyData
        data={localCaseStudies}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onSave={handleSave}
      />

      {isFetching && !isLoading && (
        <p style={{ fontSize: 12, color: "#888", marginTop: 8 }}>Updating...</p>
      )}

      <Pagination
        currentPage={page}
        totalPages={pagination.totalPages}
        onPageChange={handlePageChange}
      />

      <DeleteModal
        isOpen={isModalOpen && mode === "delete"}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedCasestudy(null);
        }}
        onConfirm={() => handleSave()}
        title="Delete Case Study"
        itemName={selectedCases?.title}
        isLoading={isDeleting}
      />
    </>
  );
};

export default CaseStudy;
